/** Shared helpers for the VMware (Enterprise Compute / Bear) console. */

import type { VmwareInstanceSummary } from '@/api/vmware/instances';

export type InstanceTone = 'success' | 'danger' | 'warning' | 'muted' | 'info';

/**
 * Map a vSphere power state to a display tone.
 * Accepts both vSphere camelCase ("poweredOn") and UPPER_SNAKE ("POWERED_ON").
 */
export function vmTone(status?: string): InstanceTone {
  switch (String(status ?? '').toUpperCase()) {
    case 'POWERED_ON':
    case 'POWEREDON':
    case 'RUNNING':
      return 'success';
    case 'SUSPENDED':
      return 'warning';
    case 'ERROR':
    case 'FAILED':
      return 'danger';
    case 'POWERED_OFF':
    case 'POWEREDOFF':
    case 'OFF':
    default:
      return 'muted';
  }
}

/**
 * Human label for a vSphere power state.
 * "poweredOn" → "Powered on", "POWERED_ON" → "Powered on".
 */
export function vmLabel(status?: string | null): string {
  if (!status) return 'Unknown';
  // Normalise camelCase vSphere values ("poweredOn" → "POWERED_ON") then
  // format the UPPER_SNAKE form as title-case words.
  const normalised = toUpperSnake(String(status));
  return normalised
    .split('_')
    .join(' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Coarse bucket for health counts. */
export function vmBucket(status?: string): 'running' | 'stopped' | 'error' | 'other' {
  switch (String(status ?? '').toUpperCase()) {
    case 'POWERED_ON':
    case 'POWEREDON':
    case 'RUNNING':
      return 'running';
    case 'POWERED_OFF':
    case 'POWEREDOFF':
    case 'OFF':
    case 'SUSPENDED':
      return 'stopped';
    case 'ERROR':
    case 'FAILED':
      return 'error';
    default:
      return 'other';
  }
}

/**
 * Normalise vSphere camelCase power state to UPPER_SNAKE.
 * "poweredOn" → "POWERED_ON", "poweredOff" → "POWERED_OFF", etc.
 * Already-upper strings pass through unchanged.
 */
export function toUpperSnake(s: string): string {
  // Insert underscore only at a camelCase boundary (lower/digit → Upper), so
  // "poweredOn" → "POWERED_ON" but an already-UPPER_SNAKE "POWERED_ON" passes
  // through unchanged (inserting before EVERY uppercase letter would explode it
  // into "P_O_W_E_R_E_D_O_N" → "P o w e r e d o n").
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toUpperCase();
}

/**
 * Pick the first public IPv4 address from summary.summaryItems[ipAddressLabel].
 *
 * The summaryView.ipAddressLabel value is an array of IP strings, e.g.:
 *   ["103.151.240.76", "176.99.3.34", "fe80::250:56ff:feb6:a77a"]
 *
 * We skip:
 *   - Link-local IPv6 (fe80::)
 *   - Any address containing ':' (all IPv6)
 *
 * This mirrors the ipAddressExtract logic in @console ManageOverview (~L534).
 */
export function pickPublicIpv4(summary?: VmwareInstanceSummary): string {
  if (!summary) return '';
  const item = summary.summaryItems?.find((x) => x.label === 'summaryView.ipAddressLabel');
  if (!item) return '';
  const list: string[] = Array.isArray(item.value) ? item.value : (typeof item.value === 'string' ? [item.value] : []);
  // Return first IPv4 (no colon = not IPv6).
  return list.find((ip) => typeof ip === 'string' && !ip.includes(':')) ?? '';
}

/**
 * Return all IPs from summaryItems.ipAddressLabel, IPv4 first then IPv6,
 * skipping link-local (fe80::). Used by OverviewTab to show the full list.
 */
export function allIps(summary?: VmwareInstanceSummary): string[] {
  if (!summary) return [];
  const item = summary.summaryItems?.find((x) => x.label === 'summaryView.ipAddressLabel');
  if (!item) return [];
  const list: string[] = Array.isArray(item.value) ? item.value : (typeof item.value === 'string' ? [item.value] : []);
  // Filter out link-local fe80:: addresses.
  return list.filter((ip) => typeof ip === 'string' && !ip.toLowerCase().startsWith('fe80'));
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
